import React, { useCallback, useEffect, useState } from 'react';
import Header from '../Header';
import {
    listIdpsByContract,
    updateIdpManager, getIdp,
} from '../../api/idps';
import IdpViewModal from '../../components/idp/IdpViewModal';
import { useNavigate, useParams } from 'react-router-dom';

// Manager view: focuses on a single employee contract passed via route (/idp/contract/:contractId)
export default function EmployeeIdps() {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [contract, setContract] = useState(null);
    const [idps, setIdps] = useState([]);

    const [modalOpen, setModalOpen] = useState(false);
    const [current, setCurrent] = useState(null);

    const loadContractIdps = useCallback(async () => {
        if (!contractId) {
            setContract(null);
            setIdps([]);
            setErr('No contract selected.');
            setLoading(false);
            return;
        }

        setLoading(true);
        setErr('');
        try {
            const res = await listIdpsByContract(contractId);
            setContract(res?.contract ?? null);
            setIdps(Array.isArray(res?.data) ? res.data : []);
        } catch (e) {
            setContract(null);
            setIdps([]);
            setErr(e?.response?.data?.message || 'Failed to load employee IDPs.');
        } finally {
            setLoading(false);
        }
    }, [contractId]);

    useEffect(() => { loadContractIdps(); }, [loadContractIdps]);

    const openView = (row) => {
        setCurrent(row);
        setModalOpen(true);
    };

    const handleSaveManager = async (payload, id) => {
        await updateIdpManager(id, payload);
        await loadContractIdps();
    };

    const employeeName = contract?.user?.name ?? 'Employee';
    const employeeEmail = contract?.user?.email ?? '';

    return (
        <>
            <Header label={<p className="py-0 my-0">Teams – Individual Development Plans</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">

                        <div className="card">
                            <div className="card-header d-flex align-items-center justify-content-between">
                                <div>
                                    <div className="fw-semibold">{employeeName} — IDPs</div>
                                    {employeeEmail && <div className="text-muted small">{employeeEmail}</div>}
                                </div>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => navigate('/idp/managed')}
                                >
                                    Back to Team
                                </button>
                            </div>
                            <div className="card-body">
                                {err && <div className="alert alert-danger">{err}</div>}
                                {loading ? (
                                    <div className="text-muted">Loading IDPs…</div>
                                ) : !idps?.length ? (
                                    <div className="text-muted">No IDPs found for this employee.</div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-sm table-hover align-middle">
                                            <thead className="table-light">
                                            <tr>
                                                <th style={{ width: 60 }}>#</th>
                                                <th>Development Objective</th>
                                                <th>Competency</th>
                                                <th style={{ width: 140 }}>Target Date</th>
                                                <th className="text-end" style={{ width: 140 }}>Actions</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {idps.map((r, idx) => (
                                                <tr key={r.id}>
                                                    <td>{String(idx + 1).padStart(2, '0')}</td>
                                                    <td className="text-truncate" title={r.development_objective}>{r.development_objective}</td>
                                                    <td className="text-truncate" title={r.competency_addressed}>{r.competency_addressed}</td>
                                                    <td>{(r.target_completion_date ?? '').slice(0, 10) || '—'}</td>
                                                    <td className="text-end">
                                                        <button className="btn btn-sm btn-outline-primary" onClick={() => openView(r)}>
                                                            View
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </main>

            <IdpViewModal
                show={modalOpen}
                onClose={() => setModalOpen(false)}
                mode="manager"
                initial={current}
                onSaveOwner={async () => {}}
                onSaveManager={handleSaveManager}
                fetchOne={getIdp}
            />
        </>
    );
}
